import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

export default function RepairGuideBuilderPanel({
  productId,
  componentId,
  initialGuide = null,
  onSave,
  onClose,
  selectedPartName, // Selected 3D mesh name from scene clicks
  activeStepIndex,
  onStepIndexChange,
  activeStepActions = [],
  onUpdateStepActions,
  onSelectPart,
  gizmoMode = 'translate',
  onGizmoModeChange
}) {
  const [title, setTitle] = useState(initialGuide?.title || '');
  const [description, setDescription] = useState(initialGuide?.description || '');
  const [isPublished, setIsPublished] = useState(initialGuide?.isPublished || false);
  const [estimatedTime, setEstimatedTime] = useState(initialGuide?.estimatedTime || '');
  
  const [steps, setSteps] = useState(
    initialGuide?.steps || [
      { title: 'Initial Disassembly', notes: 'Select a 3D component and translate it or toggle its removal status.', actions: [] }
    ]
  );

  useEffect(() => {
    if (initialGuide) {
      setTitle(initialGuide.title || '');
      setDescription(initialGuide.description || '');
      setIsPublished(initialGuide.isPublished || false);
      setEstimatedTime(initialGuide.estimatedTime || '');
      setSteps(initialGuide.steps || []);
    }
  }, [initialGuide]);

  // Synchronize active step's actions with parent so ViewerCanvas can render TransformControls / translate nodes
  useEffect(() => {
    const currentStep = steps[activeStepIndex];
    
    // Compute cumulative offsets for 3D translation rendering
    const cumulative = {};
    for (let i = 0; i <= activeStepIndex; i++) {
      const step = steps[i];
      if (step && step.actions) {
        step.actions.forEach(action => {
          cumulative[action.partName] = {
            x: action.targetX || 0,
            y: action.targetY || 0,
            z: action.targetZ || 0,
            rx: action.targetRx || 0,
            ry: action.targetRy || 0,
            rz: action.targetRz || 0,
            px: action.targetPx || 0,
            py: action.targetPy || 0,
            pz: action.targetPz || 0,
            isRemoved: action.isRemoved || false
          };
        });
      }
    }

    onUpdateStepActions?.(currentStep?.actions || [], cumulative);
  }, [activeStepIndex, steps]);

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Please enter a title for the repair guide.');
      return;
    }
    const guideData = {
      id: initialGuide?.id,
      productId,
      componentId,
      title,
      description,
      isPublished,
      estimatedTime
    };
    await onSave(guideData, steps);
  };

  const addStep = () => {
    const newStep = {
      title: 'Describe your step',
      notes: '',
      actions: []
    };
    setSteps([...steps, newStep]);
    onStepIndexChange(steps.length);
  };

  const deleteStep = (indexToDelete) => {
    if (steps.length <= 1) {
      alert('A repair sequence must contain at least one step.');
      return;
    }
    const newSteps = steps.filter((_, idx) => idx !== indexToDelete);
    setSteps(newSteps);
    // Adjust active index if it was deleted or out of bounds
    if (activeStepIndex >= newSteps.length) {
      onStepIndexChange(newSteps.length - 1);
    }
  };

  const moveStep = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === steps.length - 1) return;

    const swapTarget = direction === 'up' ? index - 1 : index + 1;
    const newSteps = [...steps];
    const temp = newSteps[index];
    newSteps[index] = newSteps[swapTarget];
    newSteps[swapTarget] = temp;

    setSteps(newSteps);
    onStepIndexChange(swapTarget);
  };

  const handleStepFieldChange = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index] = {
      ...newSteps[index],
      [field]: value
    };
    setSteps(newSteps);
  };

  // Add the selected part to the current step's actions list
  const addSelectedPartToAction = () => {
    if (!selectedPartName) {
      alert('Please click on a 3D component first to select it.');
      return;
    }

    const currentStep = steps[activeStepIndex];
    if (!currentStep) return;

    const alreadyExists = currentStep.actions?.some(act => act.partName === selectedPartName);
    if (alreadyExists) {
      alert('This component is already assigned to the current step.');
      return;
    }

    const newAction = {
      partName: selectedPartName,
      targetX: 0,
      targetY: 0,
      targetZ: 0,
      targetRx: 0,
      targetRy: 0,
      targetRz: 0,
      targetPx: 0,
      targetPy: 0,
      targetPz: 0,
      isRemoved: false
    };

    const newSteps = [...steps];
    newSteps[activeStepIndex] = {
      ...currentStep,
      actions: [...(currentStep.actions || []), newAction]
    };

    setSteps(newSteps);
  };

  const removePartFromAction = (partNameToRemove) => {
    const currentStep = steps[activeStepIndex];
    if (!currentStep) return;

    const newSteps = [...steps];
    newSteps[activeStepIndex] = {
      ...currentStep,
      actions: (currentStep.actions || []).filter(act => act.partName !== partNameToRemove)
    };

    setSteps(newSteps);
  };

  const handleActionFieldChange = (partName, field, value) => {
    const currentStep = steps[activeStepIndex];
    if (!currentStep) return;

    const newSteps = [...steps];
    newSteps[activeStepIndex] = {
      ...currentStep,
      actions: (currentStep.actions || []).map(act => {
        if (act.partName === partName) {
          return { ...act, [field]: value };
        }
        return act;
      })
    };

    setSteps(newSteps);
  };

  // Symmetrical Auto-Reverse Logic:
  // Duplicates all existing disassembly steps in reverse, returning parts perfectly to origin coordinates (0, 0, 0)
  // and restoring their hidden visibility (isRemoved = false) to complete the assembly guide loop!
  const generateReverseAssemblySteps = () => {
    if (steps.length === 0) return;

    const reverseSteps = [...steps].reverse().map((origStep, idx) => {
      // Invert all actions back to origin offsets!
      const reversedActions = (origStep.actions || []).map(act => ({
        partName: act.partName,
        targetX: 0,
        targetY: 0,
        targetZ: 0,
        targetRx: 0,
        targetRy: 0,
        targetRz: 0,
        targetPx: act.targetPx || 0,
        targetPy: act.targetPy || 0,
        targetPz: act.targetPz || 0,
        isRemoved: false // Restored back to visible!
      }));

      return {
        title: `Reassembly: ${origStep.title?.replace('Disassembly: ', '') || `Step ${steps.length + idx + 1}`}`,
        notes: `Reverse actions to carefully re-install components from: ${origStep.title || `Step ${steps.length - idx}`}.`,
        actions: reversedActions
      };
    });

    setSteps([...steps, ...reverseSteps]);
    alert(`Successfully generated ${reverseSteps.length} reassembly steps in reverse order!`);
  };

  // Callback whenever the user drags the mesh with TransformControls in the 3D Canvas
  useEffect(() => {
    // We bind a global window listener so index.jsx can dispatch the interactive drag coordinates down cleanly!
    const handlePartMoved = (e) => {
      const { partName, dx, dy, dz, drx, dry, drz } = e.detail;
      const currentStep = steps[activeStepIndex];
      if (!currentStep) return;

      const hasAction = currentStep.actions?.some(act => act.partName === partName);
      if (!hasAction) return;

      const newSteps = [...steps];
      newSteps[activeStepIndex] = {
        ...currentStep,
        actions: (currentStep.actions || []).map(act => {
          if (act.partName === partName) {
            return {
              ...act,
              targetX: dx !== null && dx !== undefined ? Number(dx.toFixed(4)) : (act.targetX || 0),
              targetY: dy !== null && dy !== undefined ? Number(dy.toFixed(4)) : (act.targetY || 0),
              targetZ: dz !== null && dz !== undefined ? Number(dz.toFixed(4)) : (act.targetZ || 0),
              targetRx: drx !== null && drx !== undefined ? Number(drx.toFixed(4)) : (act.targetRx || 0),
              targetRy: dry !== null && dry !== undefined ? Number(dry.toFixed(4)) : (act.targetRy || 0),
              targetRz: drz !== null && drz !== undefined ? Number(drz.toFixed(4)) : (act.targetRz || 0)
            };
          }
          return act;
        })
      };
      setSteps(newSteps);
    };

    window.addEventListener('repair-part-moved', handlePartMoved);
    return () => {
      window.removeEventListener('repair-part-moved', handlePartMoved);
    };
  }, [steps, activeStepIndex]);

  // Symmetrical: Listen for captured camera coordinates and append to step's cameraAngles array
  useEffect(() => {
    const handleCameraCaptured = (e) => {
      const { position, target } = e.detail;
      const currentStep = steps[activeStepIndex];
      if (!currentStep) return;

      const newAngle = {
        position: [Number(position.x.toFixed(4)), Number(position.y.toFixed(4)), Number(position.z.toFixed(4))],
        target: [Number(target.x.toFixed(4)), Number(target.y.toFixed(4)), Number(target.z.toFixed(4))]
      };

      const newSteps = [...steps];
      newSteps[activeStepIndex] = {
        ...currentStep,
        cameraAngles: [...(currentStep.cameraAngles || []), newAngle]
      };
      setSteps(newSteps);
      alert('🎥 Successfully captured and saved new 3D camera angle to this step!');
    };

    window.addEventListener('camera-captured', handleCameraCaptured);
    return () => {
      window.removeEventListener('camera-captured', handleCameraCaptured);
    };
  }, [steps, activeStepIndex]);

  return (
    <div className="flex flex-col h-full bg-slate-950 text-slate-100 p-4 border-r border-slate-800 w-[330px] shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Settings" className="text-blue-400 w-5 h-5" />
          <h3 className="font-bold text-sm tracking-wide uppercase text-white">Guide Authoring</h3>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
          <Icon name="X" className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4 flex flex-col flex-1 overflow-hidden">
        {/* Guide Meta Details */}
        <div className="space-y-3 pb-3 border-b border-slate-900 flex-shrink-0">
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Guide Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Fork Tube Seal Replacement"
              className="w-full bg-slate-900 border border-slate-800 rounded px-2.5 py-1.5 text-xs text-white outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide a high-level summary of the process..."
              className="w-full h-14 bg-slate-900 border border-slate-800 rounded px-2.5 py-1.5 text-xs text-white outline-none focus:border-blue-500 resize-none"
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex-1">
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Estimated Time (mins)</label>
              <input
                type="number"
                min="0"
                value={estimatedTime}
                onChange={(e) => setEstimatedTime(e.target.value ? parseInt(e.target.value, 10) : '')}
                placeholder="e.g. 45"
                className="w-full bg-slate-900 border border-slate-800 rounded px-2.5 py-1 text-xs text-white outline-none focus:border-blue-500 font-mono"
              />
            </div>
            
            <div className="flex flex-col items-end justify-center pt-4">
              <label className="text-[10px] font-bold uppercase text-slate-400 mb-1">Publish?</label>
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="rounded bg-slate-900 border-slate-800 text-blue-500 focus:ring-0 w-4 h-4 cursor-pointer"
              />
            </div>
          </div>
        </div>

        {/* Steps Management List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-[10px] font-bold uppercase text-slate-400">Guide Steps ({steps.length})</h4>
            <button
              onClick={addStep}
              className="text-[10px] font-bold text-blue-400 hover:text-blue-300 flex items-center gap-1 cursor-pointer"
            >
              + Add Step
            </button>
          </div>

          <div className="flex items-center gap-1.5 bg-slate-900 p-1 rounded-lg overflow-x-auto max-w-full">
            {steps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => onStepIndexChange(idx)}
                className={`px-3 py-1 rounded text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                  idx === activeStepIndex
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-slate-950 text-slate-400 hover:bg-slate-850 hover:text-slate-200'
                }`}
              >
                Step {idx + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Active Step Details */}
        {steps[activeStepIndex] && (
          <div className="bg-slate-900/40 border border-slate-900 rounded-lg p-3 space-y-3 flex-1 flex flex-col">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-black uppercase text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded flex items-center gap-1">
                Active Step {activeStepIndex + 1}
                {steps[activeStepIndex].cameraAngles && steps[activeStepIndex].cameraAngles.length > 0 && (
                  <span title={`${steps[activeStepIndex].cameraAngles.length} camera angle(s) captured!`} className="text-emerald-400 font-bold flex items-center gap-0.5">
                    📷<span className="text-[8px] font-black">{steps[activeStepIndex].cameraAngles.length}</span>
                  </span>
                )}
              </span>
              <div className="flex items-center gap-2">
                <button
                  disabled={activeStepIndex === 0}
                  onClick={() => moveStep(activeStepIndex, 'up')}
                  className="text-slate-400 hover:text-white disabled:opacity-30 disabled:hover:text-slate-400"
                  title="Move step up"
                >
                  ▲
                </button>
                <button
                  disabled={activeStepIndex === steps.length - 1}
                  onClick={() => moveStep(activeStepIndex, 'down')}
                  className="text-slate-400 hover:text-white disabled:opacity-30 disabled:hover:text-slate-400"
                  title="Move step down"
                >
                  ▼
                </button>
                <button
                  onClick={() => deleteStep(activeStepIndex)}
                  className="text-red-400 hover:text-red-300"
                  title="Delete this step"
                >
                  🗑️
                </button>
              </div>
            </div>

            <div>
              <label className="block text-[9px] font-bold uppercase text-slate-500 mb-0.5">Step Title</label>
              <input
                type="text"
                value={steps[activeStepIndex].title}
                onChange={(e) => handleStepFieldChange(activeStepIndex, 'title', e.target.value)}
                placeholder="e.g. Unbolt Outer Caliper Mounts"
                className="w-full bg-slate-950 border border-slate-850 rounded px-2 py-1 text-xs text-white outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-[9px] font-bold uppercase text-slate-500 mb-0.5">Step Notes / Instructions</label>
              <textarea
                value={steps[activeStepIndex].notes}
                onChange={(e) => handleStepFieldChange(activeStepIndex, 'notes', e.target.value)}
                placeholder="List required torque specs, safety notes, or tool sizes..."
                className="w-full h-16 bg-slate-950 border border-slate-850 rounded px-2 py-1 text-xs text-white outline-none focus:border-blue-500 resize-none"
              />
            </div>

            {/* Symmetrical Multiple Camera Views Manager */}
            <div className="bg-slate-950 border border-slate-850 rounded p-1.5 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-bold text-slate-400 flex items-center gap-0.5">
                  🎥 Step Camera Angles ({(steps[activeStepIndex].cameraAngles || []).length}):
                </span>
                <button
                  type="button"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('request-camera-capture'));
                  }}
                  className="text-[9px] font-black text-blue-400 hover:text-blue-300 flex items-center gap-0.5 cursor-pointer"
                  title="Saves current 3D viewport zoom, angle, and focus coordinates into this step!"
                >
                  + Capture View
                </button>
              </div>
              
              {(!steps[activeStepIndex].cameraAngles || steps[activeStepIndex].cameraAngles.length === 0) ? (
                <p className="text-[8px] text-slate-500 italic">No camera angles captured for this step.</p>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {steps[activeStepIndex].cameraAngles.map((ang, aIdx) => (
                    <div
                      key={aIdx}
                      className="flex items-center bg-slate-900 border border-slate-800 rounded px-1.5 py-0.5 gap-1 group"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('apply-step-camera', { detail: ang }));
                        }}
                        className="text-[9px] font-bold text-slate-300 hover:text-blue-400 transition-colors flex items-center gap-0.5 cursor-pointer"
                        title="Preview this view angle in 3D canvas"
                      >
                        Angle {aIdx + 1}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const currentStep = steps[activeStepIndex];
                          const newSteps = [...steps];
                          newSteps[activeStepIndex] = {
                            ...currentStep,
                            cameraAngles: (currentStep.cameraAngles || []).filter((_, idx) => idx !== aIdx)
                          };
                          setSteps(newSteps);
                        }}
                        className="text-[9px] text-red-500 hover:text-red-400 opacity-50 group-hover:opacity-100 transition-opacity ml-0.5"
                        title="Delete this angle"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Part assignments inside active step */}
            <div className="border-t border-slate-850 pt-2 flex flex-col flex-1">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[9px] font-bold uppercase text-slate-400">Step Component Actions</span>
                <button
                  onClick={addSelectedPartToAction}
                  disabled={!selectedPartName}
                  className="text-[9px] font-bold text-green-400 hover:text-green-300 disabled:opacity-40 disabled:hover:text-green-400 cursor-pointer flex items-center gap-0.5"
                  title={selectedPartName ? `Assign ${selectedPartName}` : 'Click on 3D component mesh to select first!'}
                >
                  + Assign Selected
                </button>
              </div>

              {/* Selected component indicator helper */}
              {selectedPartName ? (
                <div className="space-y-1.5 mb-2">
                  <div className="text-[8px] bg-green-500/10 border border-green-500/20 text-green-400 rounded px-1.5 py-0.5 truncate">
                    Selected in 3D: <span className="font-bold">{selectedPartName}</span>
                  </div>
                  
                  {/* Tool Gizmo Toggle (Move/Rotate) */}
                  <div className="flex items-center justify-between bg-slate-900 border border-slate-850 rounded p-1.5">
                    <span className="text-[9px] font-bold text-slate-400">Gizmo Tool:</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => onGizmoModeChange?.('translate')}
                        className={`px-2 py-0.5 rounded text-[8px] font-black uppercase transition-all cursor-pointer ${
                          gizmoMode === 'translate' ? 'bg-blue-600 text-white shadow' : 'bg-slate-950 text-slate-500 hover:text-slate-200'
                        }`}
                      >
                        Move
                      </button>
                      <button
                        onClick={() => onGizmoModeChange?.('rotate')}
                        className={`px-2 py-0.5 rounded text-[8px] font-black uppercase transition-all cursor-pointer ${
                          gizmoMode === 'rotate' ? 'bg-blue-600 text-white shadow' : 'bg-slate-950 text-slate-500 hover:text-slate-200'
                        }`}
                      >
                        Rotate
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-[8px] text-slate-500 italic mb-2">
                  * Click any part on 3D model above to select and assign to step actions.
                </div>
              )}

              {/* Actions list */}
              <div className="space-y-1.5 overflow-y-auto flex-1 pr-0.5">
                {(steps[activeStepIndex].actions || []).length === 0 ? (
                  <p className="text-[10px] text-slate-500 italic py-2 text-center">No component actions assigned to this step yet.</p>
                ) : (
                  (steps[activeStepIndex].actions || []).map(act => (
                    <div key={act.partName} className="bg-slate-950/80 border border-slate-850 rounded p-1.5 space-y-1">
                      <div className="flex items-center justify-between gap-1">
                        <span 
                          onClick={() => onSelectPart?.(act.partName)}
                          className="text-[10px] font-bold text-white truncate max-w-[150px] cursor-pointer hover:text-blue-400 hover:underline transition-colors"
                          title={`Click to select ${act.partName} in 3D scene`}
                        >
                          {act.partName}
                        </span>
                        <button
                          onClick={() => removePartFromAction(act.partName)}
                          className="text-[10px] text-red-500 hover:text-red-400"
                          title="Remove part action"
                        >
                          ✕
                        </button>
                      </div>

                      {/* Translations coords live HUD info */}
                      <div className="flex items-center justify-between gap-2 text-[9px] border-b border-slate-900 pb-1">
                        <div className="text-slate-400">
                          ΔX: <span className="text-blue-400 font-mono">{(act.targetX || 0).toFixed(3)}</span>
                        </div>
                        <div className="text-slate-400">
                          ΔY: <span className="text-blue-400 font-mono">{(act.targetY || 0).toFixed(3)}</span>
                        </div>
                        <div className="text-slate-400">
                          ΔZ: <span className="text-blue-400 font-mono">{(act.targetZ || 0).toFixed(3)}</span>
                        </div>
                      </div>

                      {/* Rotation coords live HUD info */}
                      <div className="flex items-center justify-between gap-2 text-[9px] border-b border-slate-900 pb-1">
                        <div className="text-slate-400">
                          ΔRX: <span className="text-purple-400 font-mono">{(act.targetRx || 0).toFixed(1)}°</span>
                        </div>
                        <div className="text-slate-400">
                          ΔRY: <span className="text-purple-400 font-mono">{(act.targetRy || 0).toFixed(1)}°</span>
                        </div>
                        <div className="text-slate-400">
                          ΔRZ: <span className="text-purple-400 font-mono">{(act.targetRz || 0).toFixed(1)}°</span>
                        </div>
                      </div>

                      {/* Pivot (Hinge) coordinates input */}
                      <div className="border-b border-slate-900 pb-1.5 pt-0.5">
                        <span className="text-[8px] text-slate-500 font-bold uppercase block mb-1">Hinge Pivot Point (Relative Offset)</span>
                        <div className="grid grid-cols-3 gap-1">
                          <div>
                            <label className="text-[7px] text-slate-500 uppercase block">Pivot X</label>
                            <input
                              type="number"
                              step="0.01"
                              value={act.targetPx !== undefined ? act.targetPx : 0}
                              onChange={(e) => handleActionFieldChange(act.partName, 'targetPx', parseFloat(e.target.value) || 0)}
                              className="w-full bg-slate-900 border border-slate-800 rounded text-[9px] text-white px-1 py-0.5 outline-none focus:border-purple-500 text-center font-mono"
                            />
                          </div>
                          <div>
                            <label className="text-[7px] text-slate-500 uppercase block">Pivot Y</label>
                            <input
                              type="number"
                              step="0.01"
                              value={act.targetPy !== undefined ? act.targetPy : 0}
                              onChange={(e) => handleActionFieldChange(act.partName, 'targetPy', parseFloat(e.target.value) || 0)}
                              className="w-full bg-slate-900 border border-slate-800 rounded text-[9px] text-white px-1 py-0.5 outline-none focus:border-purple-500 text-center font-mono"
                            />
                          </div>
                          <div>
                            <label className="text-[7px] text-slate-500 uppercase block">Pivot Z</label>
                            <input
                              type="number"
                              step="0.01"
                              value={act.targetPz !== undefined ? act.targetPz : 0}
                              onChange={(e) => handleActionFieldChange(act.partName, 'targetPz', parseFloat(e.target.value) || 0)}
                              className="w-full bg-slate-900 border border-slate-800 rounded text-[9px] text-white px-1 py-0.5 outline-none focus:border-purple-500 text-center font-mono"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Hide/Remove trigger */}
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-[8px] text-slate-500 uppercase">Hide after step?</span>
                        <input
                          type="checkbox"
                          checked={act.isRemoved}
                          onChange={(e) => handleActionFieldChange(act.partName, 'isRemoved', e.target.checked)}
                          className="rounded bg-slate-900 border-slate-800 text-blue-500 focus:ring-0 w-3 h-3 cursor-pointer"
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Global Authoring Actions */}
        <div className="space-y-2 border-t border-slate-900 pt-3 mt-auto">
          <Button
            variant="ghost"
            size="sm"
            className="w-full bg-slate-900 border border-slate-800 text-slate-200 text-xs"
            onClick={generateReverseAssemblySteps}
          >
            🔄 Generate Reverse Assembly
          </Button>

          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 bg-slate-900 border border-slate-800 text-slate-200"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-bold"
              onClick={handleSave}
            >
              Save Guide
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
